import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import MapView, { MapType, Marker, Polyline } from "react-native-maps";
import {
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import {
  checkErrors,
  getDistanceFromLatLonInMeters,
  loadSound,
  playSoundForAWhile,
  secondsToDatetimeString,
} from "../../Functions/MapFunctions";
import ModalPuntInici from "../Modals/ModalPuntInici";
import globalstyle from "../../../Styles/Globals/globals";
import ModalGhostEnd from "../Modals/ModalGhostEnd";
import { RenderTimer } from "../GhostPoint/GhostPoint";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import ModalFiltres from "../Modals/ModalFiltres";
import ModalEtsAprop from "../Modals/ModalEtsAprop";
import ModalItinerariComplet from "../Modals/ModalItinerarioCompleto";
import { useGlobal } from "../../Context/globalContext";
import { useAuth } from "../../Context/authContext";
import HintModal from "../Modals/HintModal";
import { Audio } from "expo-av";
import CompliceModal from "../Modals/CompliceModal";
import Alert from "@/Components/Components/Alert";

import {
  generateRandomString,
  postStadistic,
} from "@/Components/Functions/utils";
import AsyncStorage from "@react-native-async-storage/async-storage";
import * as FileSystem from "expo-file-system";
import { parseString } from "react-native-xml2js";
import { IPoi } from "@/types/poiTypes";
import useLocation from "@/hooks/useLocation";
import { FontAwesome } from "@expo/vector-icons";
import constants, {
  MODAL_NO_VISIBLE,
  MODAL_TORNAT_OBRIR,
  MODAL_VISIBLE,
} from "@/config/constants";
import { getPoiImage } from "@/Components/Functions/getPoiImage";
import ModalPremis from "@/Components/Screens/Modals/ModalPremis";
import { GOOGLE_MAPS_API_KEY } from "@/config/config";
import { RFeature, RLayerTileWebGL, RLayerVector, RMap, RStyle } from "rlayers";
import { Point } from "ol/geom";
import { fromLonLat } from "ol/proj";
import { Feature } from "ol";

const UserLocationMarker = ({ coordinates, zoom }) => {
  const size = Math.max(30, Math.min(80, 40 * Math.pow(1.5, zoom - 15)));
  return (
    <Marker coordinate={coordinates}>
      <FontAwesome name={"circle"} size={size} color={"#1380FF"} />
    </Marker>
  );
};

const UserLocationMarkerMapaPersonalitzat = ({ coordinates, zoom }) => {
  const { imatgeRecurs } = useGlobal();

  const zoomStep = Math.round(zoom * 2) / 2;
  const markerSize = Math.max(12, Math.min(30, 15 * Math.pow(1.4, zoom - 15)));

  const feature = new Feature({
    geometry: new Point(coordinates),
  });

  const RIconAny = RStyle.RIcon as any;

  return (
    <RLayerVector key={`user-loc-${zoomStep}`}>
      <RStyle.RStyle>
        <RIconAny src={imatgeRecurs("posicio_actual")} width={markerSize} />
      </RStyle.RStyle>

      <RFeature feature={feature} />
    </RLayerVector>
  );
};

const GoogleMapsView = (props: {
  mapType:
    | "hybrid"
    | "mutedStandard"
    | "none"
    | "satellite"
    | "standard"
    | "terrain";
  region: {
    longitudeDelta: number;
    latitudeDelta: number;
    latitude: number;
    longitude: number;
  };
  coordinates: any;
  points: any;
  establiments: React.JSX.Element;
  user: any;
  currentLocation: any;
  zoom: number;
  onRegionChange?: (region: any) => void;
}) => (
  <MapView
    provider={"google"}
    style={globalstyle.container}
    showsUserLocation={true}
    mapType={props.mapType}
    region={props.region}
    initialRegion={props.region}
    loadingEnabled={false}
    googleMapsApiKey={GOOGLE_MAPS_API_KEY}
    onRegionChangeComplete={props.onRegionChange}
    initialCamera={{
      ...(Platform.OS !== "android"
        ? {
            center: {
              latitude: props.region.latitude,
              longitude: props.region.longitude,
            },
            zoom: 16,
          }
        : {}),
    }}
    options={{
      ...(Platform.OS === "web"
        ? {
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
          }
        : {}),
    }}
  >
    <Polyline
      coordinates={props.coordinates}
      strokeColor={"red"}
      strokeWidth={3}
    />
    {props.points}
    {props.establiments}
    {props.user.es_admin && (
      <View style={{ gap: 10, flexDirection: "row" }}>
        <Text>{props.currentLocation.latitude}</Text>
        <Text>{props.currentLocation.longitude}</Text>
      </View>
    )}
    <UserLocationMarker coordinates={props.currentLocation} zoom={props.zoom} />
  </MapView>
);

const MapaPersonalitzat = (props: {
  region: {
    longitudeDelta: number;
    latitudeDelta: number;
    latitude: number;
    longitude: number;
  };
  points: any;
  establiments: React.JSX.Element;
  user: any;
  currentLocation: any;
  layoutUrl: string;
  zoom: number;
  onZoomChange?: (zoom: number) => void;
}) => {
  const center = fromLonLat([props.region.longitude, props.region.latitude]);
  const location = fromLonLat([
    props.currentLocation.longitude,
    props.currentLocation.latitude,
  ]);

  return (
    <RMap
      width={"100%"}
      height={"100%"}
      initial={{ center, zoom: 15 }}
      maxZoom={20}
      noDefaultControls={true}
      onMoveEnd={(e) => props.onZoomChange?.(e.map.getView().getZoom() ?? 15)}
    >
      <RLayerTileWebGL
        url={props.layoutUrl}
        properties={{ label: "IternaturaStyle" }}
      />

      <UserLocationMarkerMapaPersonalitzat coordinates={location} zoom={props.zoom} />

      {props.points}
      {props.establiments}

      {props.user.es_admin && (
        <View style={{ gap: 10, flexDirection: "row" }}>
          <Text>{props.currentLocation.latitude}</Text>
          <Text>{props.currentLocation.longitude}</Text>
        </View>
      )}
    </RMap>
  );
};

export default function MapScreen({ route }: any) {
  let { itinerari } = route.params ?? {};
  itinerari ??= {
    id: 0,
    pois: [],
  };

  const { __, projecte, textRecurs, imatgeRecurs } = useGlobal();
  const { user, savePerfil, logout, refreshUser } = useAuth();
  const navigation = useNavigation<any>();
  const isFocused = useIsFocused();
  const isFocusedRef = useRef(isFocused);
  useEffect(() => { isFocusedRef.current = isFocused; }, [isFocused]);

  const myPois = itinerari.id
    ? (user.progres?.itineraris?.[itinerari.id]?.pois ?? {})
    : Object.values(user.progres?.itineraris ?? {})
        ?.map((i: any) => i?.pois ?? {})
        ?.reduce((a: any, b: any) => ({ ...a, ...b }), {});

  const itinerariCompletat: boolean =
    Object.values(user.progres?.itineraris?.[itinerari.id]?.pois || {}).length ===
    (itinerari.pois?.length ?? 0);

  const poisNoCarregats = itinerari.id > 0 && (!itinerari.pois || itinerari.pois.length === 0);
  const progresCorrupte = itinerari.id > 0 && user.progres?.itineraris == null;
  const hiHaErrorDeDades = poisNoCarregats || progresCorrupte;

  const currentPoiIndex = Object.values(myPois).filter(
    (p: IPoi) => !p.esPreguntaEncadenada,
  ).length;

  const [currentPoi, setCurrentPoi] = useState<any>(
    itinerari.pois[currentPoiIndex] ?? {},
  );

  const [track, setTrack] = useState<any>([]);
  const {
    currentLocation,
    mapRegion,
    isLoadingLocation,
    foraDelGeofence,
    locationPermissionDenied,
  } = useLocation(itinerari, track);
  const [distanceNextPoi, setDistanceNextPoi] = useState(0);
  const [distanceExpanded, setDistanceExpanded] = useState(false);

  const [modalIniciVisible, setModalIniciVisible] = useState(
    itinerari.id && currentPoiIndex === 0,
  );
  const [modalApropVisible, setModalApropVisible] =
    useState<number>(MODAL_NO_VISIBLE);
  const [modalCompletVisible, setModalCompletVisible] = useState(false);

  /** So **/
  const [sound, setSound] = useState<Audio.Sound | null>(null);
  const soundRef = useRef<Audio.Sound | null>(null);
  const ghostLoopSoundRef = useRef<Audio.Sound | null>(null);

  const POI_FANTASMA_AMB_SO_EN_BUCLE = 47;
  const POI_FANTASMA_MAPA_ESPECIAL = 49;

  /** Establiments **/
  const [currentEstabliment, setCurrentEstabliment] = useState(null);
  const [modalComplicesVisible, setModalComplicesVisible] = useState(false);

  /** Establiments a favoritos **/
  const filteredEstabliments = projecte.establiments;

  const [modalPremisVisible, setModalPremisVisible] = useState<boolean>(false);

  /** Capes Maps **/
  const [mapType, setMapType] = useState<MapType>("satellite");
  const [zoom, setZoom] = useState(15);

  /** Modal filtres **/
  const [modalFiltresVisible, setModalFiltresVisible] = useState(false);
  const [filtreActiu, setFiltreActiu] = useState(null);

  /** Configuració Joc Fantasma - Ghost **/
  const [isCurrentPoiGhost, setIsCurrentPoiGhost] = useState<boolean>(false);
  const [activeGhostGame, setActiveGhostGame] = useState<boolean>(false);
  const [time, setTime] = useState<string>("5:00");
  const [timeNumeric, setTimeNumeric] = useState<number>(300);
  const [modalFinishGhost, setModalFinishGhost] = useState<boolean>(false);
  const [ghostStartTime, setGhostStartTime] = useState<number>(0);

  /** Mostrar pistes durant el Joc Fantasma **/
  const pistes: string[] = [];
  if (currentPoi) {
    for (let i = 0; i < 4; i++) {
      const pista = __(currentPoi, `pista${i + 1}`);

      if (pista) {
        pistes.push(pista);
      }
    }
  }

  const [botoPistes, setBotoPistes] = useState<boolean>(false);
  const [hintVisible, setHintVisible] = useState<boolean>(false);
  const [errors, setErrors] = useState<number>(0);

  const [especialMapUnlocked, setEspecialMapUnlocked] = useState<boolean>(false);

  const getLayouts = () => {
    const layouts = itinerari.mapes?.layouts || projecte.itineraris[0].mapes?.layouts;
    const filtered = (layouts ?? []).filter((l: any) => !l.especial || especialMapUnlocked);
    return filtered.sort((a: any, b: any) => (a.especial ? 1 : 0) - (b.especial ? 1 : 0));
  };

  const [currentLayoutUrl, setCurrentLayoutUrl] = useState<string>(
    getLayouts()?.[0]?.url,
  );

  const hasNavigatedOnItinerariCompletat = useRef(false);

  let timer: any = null;

  /** computed **/
  useEffect(() => setTime(secondsToDatetimeString(timeNumeric)), [timeNumeric]);

  useEffect(() => { soundRef.current = sound; }, [sound]);

  useEffect(() => {
    if (!isFocused) {
      soundRef.current?.stopAsync();
      soundRef.current?.unloadAsync();
      soundRef.current = null;
      setSound(null);

      ghostLoopSoundRef.current?.stopAsync();
      ghostLoopSoundRef.current?.unloadAsync();
      ghostLoopSoundRef.current = null;
    }
  }, [isFocused]);

  useEffect(() => {
    return () => {
      soundRef.current?.stopAsync();
      soundRef.current?.unloadAsync();

      ghostLoopSoundRef.current?.stopAsync();
      ghostLoopSoundRef.current?.unloadAsync();
    };
  }, []);

  useEffect(() => {
    if (!isCurrentPoiGhost || currentPoi.id !== POI_FANTASMA_AMB_SO_EN_BUCLE) {
      return;
    }

    const popupSoUrl = __(currentPoi, "popup_so")?.url;

    if (!popupSoUrl) {
      return;
    }

    let cancelled = false;

    Audio.Sound.createAsync(
      { uri: popupSoUrl },
      { shouldPlay: true, isLooping: true, volume: 0.5 },
    )
      .then(({ sound: loopSound }) => {
        if (cancelled) {
          loopSound.stopAsync();
          loopSound.unloadAsync();
          return;
        }
        ghostLoopSoundRef.current = loopSound;
      })
      .catch((error) => {
        console.error("Error reproduint el so en bucle del punt fantasma:", error);
      });

    return () => {
      cancelled = true;
      ghostLoopSoundRef.current?.stopAsync();
      ghostLoopSoundRef.current?.unloadAsync();
      ghostLoopSoundRef.current = null;
    };
  }, [isCurrentPoiGhost, currentPoi.id, __]);

  useEffect(() => {
    if (
      currentPoi.id !== POI_FANTASMA_MAPA_ESPECIAL ||
      currentPoi.tipus !== "PuntFantasma" ||
      !isCurrentPoiGhost
    ) {
      return;
    }

    if (!especialMapUnlocked) {
      setEspecialMapUnlocked(true);
      AsyncStorage.setItem("mapEspecialUnlocked", "true");
    }

    const allLayouts = itinerari.mapes?.layouts || projecte.itineraris[0].mapes?.layouts;
    const especialIndex = allLayouts?.findIndex((l: any) => l.especial) ?? -1;

    if (especialIndex >= 0) {
      setCurrentLayoutUrl(allLayouts[especialIndex].url);
    }
  }, [currentPoi.id, currentPoi.tipus, isCurrentPoiGhost]);

  const handleGameWin = useCallback(() => {
    playSoundForAWhile(imatgeRecurs("poi_encert_so"), undefined, setSound);
  }, [imatgeRecurs]);

  const handleGameLoss = useCallback(() => {
    playSoundForAWhile(imatgeRecurs("poi_error_so"), undefined, setSound);
  }, [imatgeRecurs]);

  const handleGhostGameFinish = useCallback(
    (win: boolean, punts: number) => {
      win ? handleGameWin() : handleGameLoss();

      clearInterval(timer);

      navigation.replace("Reward", {
        itinerari,
        poi: currentPoi,
        punts: win ? checkErrors(errors) : 0,
      });

      setIsCurrentPoiGhost(false);
      setActiveGhostGame(false);
      setBotoPistes(false);

      savePerfil({ progres: user.progres });
    },
    [
      timer,
      handleGameWin,
      handleGameLoss,
      savePerfil,
      user.progres,
      errors,
      navigation,
      itinerari,
      currentPoi,
    ],
  );

  const startGhostTimer = useCallback(() => {
    setActiveGhostGame(true);
    setBotoPistes(true);

    timer = setInterval(() => {
      setTimeNumeric((prevTime) => {
        const segonsQueHanPassat = (Date.now() - ghostStartTime) / 1000;

        const newTime =
          (user.es_admin ? 10 : currentPoi.segons) - segonsQueHanPassat;

        if (newTime <= 0) {
          clearInterval(timer);

          handleGhostGameFinish(false, 0);
        }

        return newTime;
      });
    }, 1000);
  }, [
    timer,
    handleGhostGameFinish,
    ghostStartTime,
    currentPoi.segons,
    user.es_admin,
  ]);

  useEffect(() => {
    if (!itinerari.id || !isFocused) return;

    if (itinerariCompletat) {
      if (hasNavigatedOnItinerariCompletat.current) return;
      hasNavigatedOnItinerariCompletat.current = true;

      postStadistic({
        projecte: projecte.nom,
        token: user.id,
        peticio: `${itinerari.id} ${__(itinerari, "nom")}`,
        tipus_peticio: "Fi itinerari",
        screen: "Mapa",
      });

      user.progres.itineraris[itinerari.id].fi = new Date().toISOString();
      user.progres.itineraris[itinerari.id].codiPremi = generateRandomString();

      savePerfil({ progres: user.progres });

      if (itinerari.premi?.tipus === "Trucada") {
        return navigation.replace("PuzzlePerfil", {
          itinerari,
          esFinal: true,
        });
      }

      if (itinerari.premi) {
        return navigation.navigate("Premi", {
          itinerari,
          premi: itinerari.premi,
          esFinal: true,
        });
      }
    }

    const poi = itinerari.pois[currentPoiIndex] ?? {};

    setCurrentPoi(poi);

    if (poi.tipus === "PuntFantasma" && !isCurrentPoiGhost) {
      setTimeNumeric(user.es_admin ? 10 : poi.segons);
    }
  }, [isFocused]);

  useEffect(() => {
    if (!modalIniciVisible && foraDelGeofence) {
      loadSound(imatgeRecurs("geofence_out"), setSound);
    }
  }, [modalIniciVisible, foraDelGeofence, currentLocation]);

  useEffect(() => {
    if (
      !itinerari.id ||
      modalIniciVisible ||
      modalApropVisible ||
      modalFinishGhost
    ) {
      return;
    }

    if (isFocused) {
      setTimeout(() => {
        if (isFocusedRef.current) comprovaPuntProxim();
      }, 2000);
    }
  }, [
    modalIniciVisible,
    modalApropVisible,
    modalFinishGhost,
    isCurrentPoiGhost,
    currentLocation,
  ]);

  useEffect(() => {
    if (!itinerari.id || !isCurrentPoiGhost || !currentPoi.latitud) {
      return;
    }

    if (!activeGhostGame) {
      startGhostTimer();
    }

    const distance = getDistanceFromLatLonInMeters(
      currentLocation.latitude,
      currentLocation.longitude,
      currentPoi.latitud,
      currentPoi.longitud,
    );

    if (distance < currentPoi.metres_activacio) {
      handleGhostGameFinish(true, checkErrors(errors));

      clearInterval(timer);
    }
  }, [isCurrentPoiGhost, currentLocation, activeGhostGame]);


  const handleTancaModalPuntFantasmaAcabat = useCallback(() => {
    setModalFinishGhost(false);
    setHintVisible(false);

    const poi = itinerari.pois[currentPoiIndex];

    setCurrentPoi(poi);
  }, [currentPoiIndex, itinerari.pois]);

  const GEOFENCE_TOLERANCE_FACTOR = 1.2;

  const comprovaPuntProxim = useCallback(() => {
    if (
      !currentPoi.latitud ||
      currentLocation?.latitude == null ||
      currentLocation?.longitude == null
    ) {
      return false;
    }

    const distance = getDistanceFromLatLonInMeters(
      currentLocation.latitude,
      currentLocation.longitude,
      currentPoi.latitud,
      currentPoi.longitud,
    );

    if (distance > 0) {
      setDistanceNextPoi(Math.round(distance));
    }

    // Ja està activat, res a fer
    if (isCurrentPoiGhost) {
      return;
    }

    const effectiveRadius =
      (currentPoi.metres_activacio ?? 0) * GEOFENCE_TOLERANCE_FACTOR;

    if (
      distance < effectiveRadius ||
      currentPoi.tipus === "PuntFantasma"
    ) {
      setModalApropVisible(MODAL_VISIBLE);
    }

    const arrivalRadius =
      (itinerari.metres_activacio_arribada_poi ?? 0) * GEOFENCE_TOLERANCE_FACTOR;
    if (distance < arrivalRadius) {
      loadSound(imatgeRecurs("poi_arribada"), setSound);
    }
  }, [
    currentLocation.latitude,
    currentLocation.longitude,
    currentPoi.latitud,
    currentPoi.longitud,
    currentPoi.metres_activacio,
    currentPoi.tipus,
    imatgeRecurs,
    itinerari.metres_activacio_arribada_poi,
    isCurrentPoiGhost,
  ]);

  const onPoiPress = (poi: any, poiIndex: number, realitzat: boolean) => {
    const distance = getDistanceFromLatLonInMeters(
      currentLocation.latitude,
      currentLocation.longitude,
      poi.latitud,
      poi.longitud,
    );

    if (
      !projecte.desactivar_geoposicionament &&
      !user.es_admin &&
      poi.metres_activacio > 0 &&
      distance > poi.metres_activacio
    ) {
      return Alert(
        textRecurs("poi_alerta_distancia_titulo"),
        textRecurs("poi_alerta_distancia_texto"),
      );
    }

    if (realitzat && itinerari.id) {
      return Alert("", textRecurs("poi_alerta_completat"));
    }

    // Encara no es pot fer aquest poi
    if (poiIndex != currentPoiIndex && itinerari.id) {
      return Alert("", textRecurs("poi_alerta_error"));
    }

    if (!itinerari.id) {
      setCurrentPoi(poi);
      setModalApropVisible(MODAL_TORNAT_OBRIR);
    } else {
      setModalApropVisible(MODAL_VISIBLE);
    }
  };

  const renderTrack = async () => {
    try {
      const fileUri = `${FileSystem.documentDirectory + itinerari.id}.gpx`;

      if (!(await FileSystem.getInfoAsync(fileUri)).exists) {
        return;
      }

      const content = await FileSystem.readAsStringAsync(fileUri);

      parseString(content, function (err, result) {
        const coordinates = result.gpx.trk[0].trkseg[0].trkpt.map(
          (element: any) => ({
            latitude: parseFloat(element.$.lat),
            longitude: parseFloat(element.$.lon),
          }),
        );
        setTrack(coordinates);
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!itinerari.track) return;

    renderTrack();
  }, [itinerari]);

  const handleShowInfoView = () => {
    setModalApropVisible(MODAL_TORNAT_OBRIR);
  };

  const mostraPoisFets = () => {
    const pois: any = [];

    projecte.itineraris.forEach((itinerari: any) => {
      itinerari.pois.forEach((poi: any, poiIndex: number) => {
        const realitzat = !!myPois[poi.id];

        if (!realitzat || poi.tipus === "PuntFantasma" || !poi.latitud) {
          return;
        }

        let uri = getPoiImage(
          projecte.multimedies,
          poiIndex,
          realitzat,
          currentPoiIndex,
          itinerari,
        );
        if (poi.poi_imatge?.url) uri = poi.poi_imatge.url;

        const _poi = (
          <Marker
            key={poiIndex}
            coordinate={{
              latitude: parseFloat(poi.latitud),
              longitude: parseFloat(poi.longitud),
            }}
            onPress={() => onPoiPress(poi, poiIndex, realitzat)}
          >
            <Image
              source={{ uri }}
              style={{ width: 40, height: 40, aspectRatio: 1 }}
              resizeMode="contain"
            />
          </Marker>
        );
        pois.push(_poi);
      });
    });

    return pois;
  };

  const mostraPoisFetsMapaPersonalitzat = () => {
    const pois: any = [];

    projecte.itineraris.forEach((itinerari: any) => {
      itinerari.pois.forEach((poi: any, poiIndex: number) => {
        const realitzat = !!myPois[poi.id];

        if (!realitzat || poi.tipus === "PuntFantasma" || !poi.latitud) {
          return;
        }

        const icon = getPoiImage(
          projecte.multimedies,
          poiIndex,
          realitzat,
          currentPoiIndex,
          itinerari,
        );

        const feature = new Feature({
          geometry: new Point(
            fromLonLat([parseFloat(poi.longitud), parseFloat(poi.latitud)]),
          ),
        });

        const _poi = (
          <RLayerVector key={poi.id}>
            <RStyle.RStyle>
              <RStyle.RIcon src={icon} scale={0.4} />
            </RStyle.RStyle>
            <RFeature
              feature={feature}
              onClick={(e) => onPoiPress(poi, poiIndex, realitzat)}
            />
          </RLayerVector>
        );

        pois.push(_poi);
      });
    });

    return pois;
  };

  const Points = () => {
    if (!itinerari.id) {
      return mostraPoisFets();
    }

    // El pròxim punt fantasma
    const indexPoiFantasma: number = itinerari.pois.findIndex(
      (p: any, index) => index >= currentPoiIndex && p.tipus === "PuntFantasma",
    );

    return itinerari.pois
      .map((poi: any, poiIndex: number) => {
        const realitzat = !!myPois[poi.id];

        if (
          poi.tipus === "PuntFantasma" ||
          !poi.latitud ||
          (currentPoiIndex === 0 && poiIndex !== 0)
        ) {
          return null;
        }

        // Només mostra fins al poi fantasma
        if (indexPoiFantasma >= 0 && poiIndex > indexPoiFantasma) {
          return null;
        }

        let uri = getPoiImage(
          projecte.multimedies,
          poiIndex,
          realitzat,
          currentPoiIndex,
          itinerari,
        );
        if (poi.poi_imatge?.url) uri = poi.poi_imatge.url;

        return (
          <Marker
            key={poiIndex}
            coordinate={{
              latitude: parseFloat(poi.latitud),
              longitude: parseFloat(poi.longitud),
            }}
            onPress={() => onPoiPress(poi, poiIndex, realitzat)}
          >
            <Image
              source={{ uri }}
              style={{ width: 40, height: 40, aspectRatio: 1 }}
              resizeMode="contain"
            />
          </Marker>
        );
      })
      .filter((i) => i);
  };

  const Establiments = () => {
    const handleClickMarker = (establiment: any) => {
      setCurrentEstabliment(establiment);
      setModalComplicesVisible(true);
    };

    return (
      <>
        {filteredEstabliments.map((establiment: any) => {
          if (
            establiment.latitud &&
            establiment.longitud &&
            filtreActiu && establiment.tipus === filtreActiu
          ) {
            const uri = imatgeRecurs(
              "recurs_" + establiment.tipus.replaceAll(" ", "_").toLowerCase(),
            );

            return (
              <Marker
                key={establiment.id}
                coordinate={{
                  latitude: parseFloat(establiment.latitud),
                  longitude: parseFloat(establiment.longitud),
                }}
                onPress={() => handleClickMarker(establiment)}
              >
                <Image style={{ width: 60, height: 60, aspectRatio: 1 }} source={{ uri }} resizeMode="contain" />
              </Marker>
            );
          }
          return null;
        })}
      </>
    );
  };

  const PointsMapaPersonaltizat = () => {
    if (!itinerari.id) {
      return mostraPoisFetsMapaPersonalitzat();
    }

    // El pròxim punt fantasma
    const indexPoiFantasma: number = itinerari.pois.findIndex(
      (p: any, index) => index >= currentPoiIndex && p.tipus === "PuntFantasma",
    );

    return itinerari.pois
      .map((poi: any, poiIndex: number) => {
        const realitzat = !!myPois[poi.id];

        if (
          poi.tipus === "PuntFantasma" ||
          !poi.latitud ||
          (currentPoiIndex === 0 && poiIndex !== 0)
        ) {
          return null;
        }

        // Només mostra fins al poi fantasma
        if (indexPoiFantasma >= 0 && poiIndex > indexPoiFantasma) {
          return null;
        }

        const icon = getPoiImage(
          projecte.multimedies,
          poiIndex,
          realitzat,
          currentPoiIndex,
          itinerari,
        );

        const feature = new Feature({
          geometry: new Point(
            fromLonLat([parseFloat(poi.longitud), parseFloat(poi.latitud)]),
          ),
        });

        return (
          <RLayerVector key={poi.id}>
            <RStyle.RStyle>
              <RStyle.RIcon src={icon} scale={0.4} />
            </RStyle.RStyle>
            <RFeature
              feature={feature}
              onClick={(e) => onPoiPress(poi, poiIndex, realitzat)}
            />
          </RLayerVector>
        );
      })
      .filter((i) => i);
  };

  const EstablimentsMapaPersonalitzat = () => {
    const handleClickMarker = (establiment: any) => {
      setCurrentEstabliment(establiment);
      setModalComplicesVisible(true);
    };

    return (
      <>
        {filteredEstabliments
          .filter(
            (establiment: any) =>
              establiment.latitud &&
              establiment.longitud &&
              filtreActiu && establiment.tipus === filtreActiu,
          )
          .map((establiment: any) => {
            const uri = imatgeRecurs(
              "recurs_" + establiment.tipus.replaceAll(" ", "_").toLowerCase(),
            );

            return (
              <RLayerVector key={establiment.id}>
                <RStyle.RStyle>
                  <RStyle.RIcon src={uri} scale={0.5} />
                </RStyle.RStyle>

                <RFeature
                  geometry={
                    new Point(
                      fromLonLat([
                        parseFloat(establiment.longitud),
                        parseFloat(establiment.latitud),
                      ]),
                    )
                  }
                  onClick={() => handleClickMarker(establiment)}
                />
              </RLayerVector>
            );
          })}
      </>
    );
  };

  useEffect(() => {
    AsyncStorage.getItem("mapType").then((saved) => {
      if (saved) setMapType(saved as MapType);
    });

    AsyncStorage.getItem("mapEspecialUnlocked").then((savedUnlocked) => {
      const unlocked = savedUnlocked === "true";
      if (unlocked) setEspecialMapUnlocked(true);

      AsyncStorage.getItem("mapLayoutIndex").then((saved) => {
        if (saved !== null) {
          const index = parseInt(saved, 10);
          const allLayouts = itinerari.mapes?.layouts || projecte.itineraris[0].mapes?.layouts;
          const layouts = ((allLayouts ?? []) as any[])
            .filter((l: any) => !l.especial || unlocked)
            .sort((a: any, b: any) => (a.especial ? 1 : 0) - (b.especial ? 1 : 0));
          if (layouts?.[index]) setCurrentLayoutUrl(layouts[index].url);
        }
      });
    });
  }, []);

  const handleChangeMapType = () => {
    const layouts = getLayouts();

    if (layouts?.length > 0) {
      setCurrentLayoutUrl((prev) => {
        const index = layouts.findIndex((l) => l.url === prev);
        const nextIndex = index + 1 >= layouts.length ? 0 : index + 1;
        AsyncStorage.setItem("mapLayoutIndex", String(nextIndex));
        return layouts[nextIndex].url;
      });
    }

    const nextMapType = mapType === "standard" ? "satellite" : mapType === "satellite" ? "hybrid" : "standard";
    setMapType(nextMapType);
    AsyncStorage.setItem("mapType", nextMapType);
  };

  const handleRegionChange = (region: any) => {
    const newZoom = Math.log2(360 / region.longitudeDelta);
    setZoom(newZoom);
  };

  const mostraMapaPersonalitzat = getLayouts().length > 0;

  return (
    <View style={globalstyle.container}>
      {locationPermissionDenied ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>
            {textRecurs("error_permission_location")}
          </Text>
          <TouchableOpacity style={styles.errorButton} onPress={() => Linking.openSettings()}>
            <Text style={styles.errorButtonText}>
              {textRecurs("error_permission_location_boto", [], "Obrir configuració")}
            </Text>
          </TouchableOpacity>
        </View>
      ) : !isFocused || isLoadingLocation ? (
        <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
          <ActivityIndicator size="large" color={constants.colorGreenDarkBtn} />
        </View>
      ) : hiHaErrorDeDades ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>
            {poisNoCarregats
              ? textRecurs("mapa_error_pois", [], "No s'han pogut carregar els punts de l'itinerari. Comprova la connexió.")
              : textRecurs("mapa_error_progres", [], "No s'han pogut carregar les teves dades. Comprova la connexió.")}
          </Text>
          <TouchableOpacity
            style={styles.errorButton}
            onPress={async () => { await refreshUser(); navigation.goBack(); }}
          >
            <Text style={styles.errorButtonText}>
              {textRecurs("mapa_error_recarregar", [], "Tornar a carregar")}
            </Text>
          </TouchableOpacity>
          {progresCorrupte && (
            <TouchableOpacity style={styles.errorButtonOutline} onPress={() => logout()}>
              <Text style={styles.errorButtonOutlineText}>
                {textRecurs("mapa_error_netejar", [], "Netejar dades i reiniciar sessió")}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      ) : mostraMapaPersonalitzat ? (
        <MapaPersonalitzat
          region={mapRegion}
          establiments={EstablimentsMapaPersonalitzat()}
          points={PointsMapaPersonaltizat()}
          user={user}
          currentLocation={currentLocation}
          layoutUrl={currentLayoutUrl}
          zoom={zoom}
          onZoomChange={setZoom}
        />
      ) : (
        <GoogleMapsView
          mapType={mapType}
          region={mapRegion}
          coordinates={track}
          points={Points()}
          establiments={Establiments()}
          user={user}
          currentLocation={currentLocation}
          zoom={zoom}
          onRegionChange={handleRegionChange}
        />
      )}

      {itinerari.id > 0 && !modalIniciVisible && (
        <View style={styles.liniaBotons}>
          <TouchableOpacity
            style={[styles.boto]}
            onPress={() => setModalPremisVisible(true)}
          >
            <Image
              source={{ uri: imatgeRecurs("mapa_recompensa") }}
              style={{ width: 40, height: 40 }}
              resizeMode="contain"
            />
          </TouchableOpacity>

          {isCurrentPoiGhost && (
            <TouchableOpacity style={styles.boto} onPress={handleShowInfoView}>
              <Image
                source={{ uri: imatgeRecurs("mapa_info") }}
                style={{ width: 40, height: 40 }}
                resizeMode="contain"
              />
            </TouchableOpacity>
          )}

          {botoPistes && (
            <TouchableOpacity
              style={styles.boto}
              onPress={() => setHintVisible(true)}
            >
              <Image
                source={{ uri: imatgeRecurs("mapa_pistas") }}
                style={{ width: 40, height: 40 }}
                resizeMode="contain"
              />
            </TouchableOpacity>
          )}

          {isCurrentPoiGhost && (
            <TouchableOpacity style={styles.boto} onPress={handleChangeMapType}>
              <Image
                style={{ width: 30, height: 30, aspectRatio: 1 }}
                source={{ uri: imatgeRecurs("filtros_capes") }}
                resizeMode="contain"
              />
            </TouchableOpacity>
          )}
        </View>
      )}
      <View
        style={[
          styles.liniaBotons,
          {
            top: 130,
            display: itinerari.id && !modalIniciVisible ? "flex" : "none",
          },
        ]}
      >
        <TouchableOpacity
          style={[styles.boto, styles.botoLlarg, distanceExpanded && styles.botoLlargExpanded]}
          onPress={() => setDistanceExpanded((prev) => !prev)}
        >
          <Image
            source={{ uri: imatgeRecurs("mapa_distancia") }}
            style={{ width: distanceExpanded ? 52 : 40, height: distanceExpanded ? 52 : 40 }}
            resizeMode="contain"
          />
          <Text style={distanceExpanded && styles.distanceTextExpanded}>{distanceNextPoi}m</Text>
        </TouchableOpacity>
      </View>
      {!isCurrentPoiGhost && (
        <TouchableOpacity
          style={globalstyle.arrowPosition}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={{ uri: imatgeRecurs("boton_atras") }}
            style={{ height: 30, width: 30, aspectRatio: 1 }}
            resizeMode="contain"
          />
        </TouchableOpacity>
      )}

      <RenderTimer time={time} poi={currentPoi} isVisible={isCurrentPoiGhost} />

      {!isCurrentPoiGhost && (
        <View style={globalstyle.filtersContainer}>
          <View
            style={{
              flexDirection: "row",
              backgroundColor: "white",
              paddingVertical: 20,
              paddingHorizontal: 30,
              borderRadius: 32,
              borderWidth: 1,
              justifyContent: "center",
            }}
          >
            <TouchableOpacity
              style={{ marginHorizontal: 12 }}
              onPress={() => setModalFiltresVisible(!modalFiltresVisible)}
            >
              <Image
                style={{ width: 30, height: 30, aspectRatio: 1 }}
                source={{ uri: imatgeRecurs("filtros_filtre") }}
                resizeMode="contain"
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={{
                marginHorizontal: 12,
              }}
              onPress={handleChangeMapType}
            >
              <Image
                style={{ width: 30, height: 30, aspectRatio: 1 }}
                source={{ uri: imatgeRecurs("filtros_capes") }}
                resizeMode="contain"
              />
            </TouchableOpacity>
          </View>
        </View>
      )}
      <HintModal
        modalVisible={hintVisible}
        setModalVisible={setHintVisible}
        pistes={pistes}
        errors={errors}
        setErrors={setErrors}
      />
      <CompliceModal
        modalCompliceVisible={modalComplicesVisible}
        setModalCompliceVisible={setModalComplicesVisible}
        item={currentEstabliment}
      />

      {ModalFiltres(
        modalFiltresVisible,
        setModalFiltresVisible,
        filtreActiu,
        setFiltreActiu,
      )}

      <ModalEtsAprop
        modalVisible={modalApropVisible}
        setModalVisible={setModalApropVisible}
        itinerari={itinerari}
        poi={currentPoi}
        setStartGame={setIsCurrentPoiGhost}
        setGhostStartTime={setGhostStartTime}
      />

      {modalPremisVisible && (
        <ModalPremis
          itinerari={itinerari}
          setModalVisible={setModalPremisVisible}
        />
      )}

      {ModalPuntInici(modalIniciVisible, setModalIniciVisible, itinerari)}

      {ModalGhostEnd(
        modalFinishGhost,
        handleTancaModalPuntFantasmaAcabat,
        checkErrors(errors),
        itinerari,
        currentPoi,
      )}

      {ModalItinerariComplet(modalCompletVisible, setModalCompletVisible)}
    </View>
  );
}

const styles = StyleSheet.create({
  liniaBotons: {
    flexDirection: "row",
    position: "absolute",
    top: 80,
    right: 12,
    gap: 10,
  },
  boto: {
    backgroundColor: constants.colorWhite,
    borderRadius: 4,
    padding: 10,
    width: 40,
    height: 40,
    aspectRatio: 1,
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "center",
  },
  botoLlarg: {
    width: "auto",
    minWidth: 90,
    paddingHorizontal: 10,
    flexDirection: "row",
    gap: 5,
  },
  botoLlargExpanded: {
    minWidth: 110,
    height: 56,
    borderRadius: 28,
    paddingHorizontal: 14,
  },
  distanceTextExpanded: {
    fontSize: 18,
    fontWeight: "bold",
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 32,
    gap: 16,
  },
  errorText: {
    textAlign: "center",
    fontSize: 15,
    color: constants.colorGrey,
    marginBottom: 8,
  },
  errorButton: {
    backgroundColor: constants.colorGreenDarkBtn,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    width: "100%",
    alignItems: "center",
  },
  errorButtonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "600",
  },
  errorButtonOutline: {
    borderWidth: 1,
    borderColor: constants.colorGrey,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    width: "100%",
    alignItems: "center",
  },
  errorButtonOutlineText: {
    color: constants.colorGrey,
    fontSize: 14,
  },
});
